import type { Route } from 'next';
import Link, { type LinkProps } from 'next/link';
import type { ComponentPropsWithRef } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './icon-button.module.scss';

type Appearance = 'plain' | 'outlined' | 'round';
type Common = { label: string; appearance?: Appearance; size?: 'sm' | 'md' };

export type IconButtonProps = Omit<ComponentPropsWithRef<'button'>, 'aria-label'> & Common;

/** Square 44px icon control with a required accessible name. */
export function IconButton({
  label,
  appearance = 'plain',
  size = 'md',
  className,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      {...props}
      type={type}
      aria-label={label}
      className={cx(styles.root, className)}
      data-appearance={appearance}
      data-size={size}
    />
  );
}

export type IconLinkProps<T extends string = string> = Omit<LinkProps<T>, 'aria-label' | 'href'> &
  Common & { href: Route<T> };

export function IconLink<T extends string>({
  label,
  appearance = 'plain',
  size = 'md',
  href,
  className,
  ...props
}: IconLinkProps<T>) {
  return (
    <Link
      {...props}
      href={href}
      aria-label={label}
      className={cx(styles.root, className)}
      data-appearance={appearance}
      data-size={size}
    />
  );
}
